import { action, computed, makeObservable, observable } from 'mobx'

import { RecipientType } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import {
  ILWFCCategory,
  ILWFCColumn,
  ILWFCRow,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Assignment from '~/client/src/shared/models/Assignment/Assignment'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import SitePermitAssignmentsStore from '~/client/src/shared/stores/domain/SitePermitAssignments.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import {
  CATEGORY_ROW_HEIGHT,
  ITreeNodeObj,
} from '~/client/src/shared/stores/ui/BaseList.store'
import BaseMultiBandListStore from '~/client/src/shared/stores/ui/BaseMultiBandList.store'
import { groupingCommonMapper } from '~/client/src/shared/utils/mappers'
import {
  getAppliedTagIds,
  getUserTagFilterMatcher,
} from '~/client/src/shared/utils/userTagFilter'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

const ONLY_ME_FILTER_KEY = 'OnlyMe'
const ONLY_MY_COMPANY_FILTER_KEY = 'OnlyMyCompany'

export const SUBSCRIBE_USERS_FILTER_TAG_TYPES: TagType[] = [
  TagType.Company,
  TagType.GlobalAndProjectSpecificRole,
  TagType.Team,
  TagType.Trade,
  TagType.AccountPosition,
]

export enum SubscribeUsersSelectorDataKeys {
  CHECKBOX = 'checkbox',
  USER = 'user',
}

export enum SubscribeUsersSelectorGroupingOption {
  NONE = 'None',
  COMPANY = 'Company',
  ROLE = 'Role',
  TEAM = 'Team',
  TRADE = 'Trade',
  ACCOUNT_POSITION = 'AccountPosition',
}

export enum SubscribeUsersSelectorTab {
  All = 'all',
  Subscribed = 'subscribed',
}

class SubscribeUsersSelectorStore extends BaseMultiBandListStore<User> {
  @observable private draftUserIdsByWorkflow: Map<string, Set<string>> =
    new Map()
  @observable private selectedTagIds: string[] = []

  public constructor(
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly userProjectsStore: UserProjectsStore,
    private readonly companiesStore: CompaniesStore,
    private readonly state: DesktopInitialState,
    private readonly selectedWorkflows: Workflow[],
    private readonly workflowAssignmentsStore: SitePermitAssignmentsStore,
    private readonly projectRolesStore?: ProjectRolesStore,
    private readonly projectTeamsStore?: ProjectTeamsStore,
    private readonly projectTradesStore?: ProjectTradesStore,
    private readonly tagsStore?: TagsStore,
  ) {
    super(state.subscribeUsersFilters, () => projectMembersStore.list, [
      'fullName',
      'email',
      'firstName',
      'lastName',
    ])
    makeObservable(this)
    this.resetDraftFromStore()
  }

  @observable public activeTab: SubscribeUsersSelectorTab =
    SubscribeUsersSelectorTab.All

  @action.bound
  public setActiveTab(tab: SubscribeUsersSelectorTab) {
    this.activeTab = tab
  }

  @computed
  public get areAllVisibleUsersAssignedToAll(): boolean {
    const users = this.filteredCollection
    if (!users.length) return false
    return users.every(u => this.isUserAssignedToAllSelectedWorkflows(u.id))
  }

  @action.bound
  public toggleSelectAllVisibleUsers() {
    const userIds = this.filteredCollection.map(u => u.id)
    if (!userIds.length) return

    if (this.areAllVisibleUsersAssignedToAll) {
      this.removeAssignment(userIds)
    } else {
      this.createAssignment(userIds)
    }
  }

  private ensureFilterEntry(key: string) {
    const { fieldsMap } = this.filter
    if (!fieldsMap[key]) {
      fieldsMap[key] = new UIFilterInfo()
    }
    return fieldsMap[key]
  }

  public get isOnlyMeActive(): boolean {
    return !!this.filter.fieldsMap?.[ONLY_ME_FILTER_KEY]?.isFilterActive
  }

  public get isOnlyMyCompanyActive(): boolean {
    return !!this.filter.fieldsMap?.[ONLY_MY_COMPANY_FILTER_KEY]?.isFilterActive
  }

  @action.bound
  public toggleOnlyMe() {
    const myUserId = this.state.user?.id
    const entry = this.ensureFilterEntry(ONLY_ME_FILTER_KEY)

    if (!myUserId) {
      entry.isFilterActive = false
      entry.appliedFilterOptions = []
      return
    }

    if (entry.isFilterActive) {
      entry.isFilterActive = false
      entry.appliedFilterOptions = []
      return
    }

    entry.appliedFilterOptions = [myUserId]
    entry.isFilterActive = true
  }

  @action.bound
  public toggleOnlyMyCompany() {
    const myCompanyId = UserProject.getCompanyId(
      this.state.user,
      this.userProjectsStore,
    )
    const entry = this.ensureFilterEntry(ONLY_MY_COMPANY_FILTER_KEY)

    if (!myCompanyId) {
      entry.isFilterActive = false
      entry.appliedFilterOptions = []
      return
    }

    const companyMemberIds = this.projectMembersStore.list
      .filter(
        u =>
          UserProject.getCompanyId(u, this.userProjectsStore) === myCompanyId,
      )
      .map(u => u.id)

    if (entry.isFilterActive) {
      entry.isFilterActive = false
      entry.appliedFilterOptions = []
      return
    }

    entry.appliedFilterOptions = companyMemberIds
    entry.isFilterActive = true
  }

  @computed
  public get subscribedUsers(): User[] {
    return this.projectMembersStore.list.filter(user =>
      this.isUserAssignedToAnySelectedWorkflow(user.id),
    )
  }

  @computed
  public get subscribedCount(): number {
    return this.subscribedUsers.length
  }

  protected get instancesInPeriodInterval(): User[] {
    const base =
      this.activeTab === SubscribeUsersSelectorTab.Subscribed
        ? this.subscribedUsers
        : this.projectMembersStore.list

    if (!this.appliedTagIds.length || !this.tagsStore) {
      return base
    }

    const usersByTagId = this.tagsStore.allUsersByRelatedTagIdMap
    const matches = getUserTagFilterMatcher(
      this.appliedTagIds,
      SUBSCRIBE_USERS_FILTER_TAG_TYPES,
      this.tagsStore.tagListsWithDefaultsTagsMap,
      tagId => (usersByTagId[tagId] || []).map(user => user.id),
    )
    return base.filter(u => matches(u.id))
  }

  @action.bound
  public toggleUserAssingment(userId: string) {
    if (!userId || !this.selectedWorkflows?.length) {
      return
    }

    const isDirectlyAssignedToAll =
      this.isUserAssignedToAllSelectedWorkflows(userId)

    if (isDirectlyAssignedToAll) {
      return this.removeAssignment([userId])
    }

    this.createAssignment([userId])
  }

  @computed
  public get appliedTagIds(): string[] {
    return getAppliedTagIds(
      this.selectedTagIds,
      SUBSCRIBE_USERS_FILTER_TAG_TYPES,
      this.tagsStore?.tagListsWithDefaultsTagsMap ?? {},
    )
  }

  @action.bound
  public setSelectedTagIds(tagIds: string[]) {
    this.selectedTagIds = Array.from(new Set(tagIds || []))
  }

  public isAssigned(entityId: string, userId: string): boolean {
    const set = this.draftUserIdsByWorkflow.get(entityId)
    if (!set) return false
    return set.has(userId)
  }

  public isUserAssignedToAllSelectedWorkflows(userId: string): boolean {
    if (!this.selectedWorkflows?.length) {
      return false
    }

    return this.selectedWorkflows.every(workflow =>
      this.isAssigned(workflow.id, userId),
    )
  }

  public isUserAssignedToAnySelectedWorkflow(userId: string): boolean {
    if (!this.selectedWorkflows?.length) {
      return false
    }

    return this.selectedWorkflows.some(workflow =>
      this.isAssigned(workflow.id, userId),
    )
  }

  @action.bound
  public toggleCategoryAssingment(category: ILWFCCategory) {
    if (!category?.isChecked) {
      this.createAssignment(category.idsToSelect)
    } else {
      this.removeAssignment(category.idsToSelect)
    }
    this.internalToggleCategory(category)
  }

  private createAssignment(userIds: string[] = []) {
    const uniqueUserIds = Array.from(new Set((userIds || []).filter(Boolean)))
    if (!uniqueUserIds.length || !this.selectedWorkflows?.length) {
      return
    }

    this.selectedWorkflows.forEach(workflow => {
      const workflowId = workflow.id
      const current = this.draftUserIdsByWorkflow.get(workflowId) || new Set()
      uniqueUserIds.forEach(uid => current.add(uid))
      this.draftUserIdsByWorkflow.set(workflowId, new Set(current))
    })
  }

  private removeAssignment(userIds: string[] = []) {
    const uniqueUserIds = Array.from(new Set((userIds || []).filter(Boolean)))
    if (!uniqueUserIds.length || !this.selectedWorkflows?.length) {
      return
    }

    this.selectedWorkflows.forEach(workflow => {
      const workflowId = workflow.id
      const current = new Set(this.draftUserIdsByWorkflow.get(workflowId) || [])
      uniqueUserIds.forEach(uid => current.delete(uid))
      this.draftUserIdsByWorkflow.set(workflowId, current)
    })

    return
  }

  protected createTreeNodeCategoryRow(
    categoryId: string,
    categoryName: string,
    categoryObject: any,
    level: number,
    objects: User[],
  ): ILWFCRow {
    const countLabel = this.shouldShowInstanceCount
      ? ` (${objects.length})`
      : ''
    const label = categoryName + countLabel

    const isChecked = objects.every(inst =>
      this.isUserAssignedToAllSelectedWorkflows(inst[this.idKey]),
    )

    const isPartialChecked =
      !isChecked &&
      objects.some(inst =>
        this.isUserAssignedToAnySelectedWorkflow(inst[this.idKey]),
      )

    const category: ILWFCCategory = {
      categoryId,
      shortCategoryLabel: categoryName,
      categoryLabel: label,
      countLabel: countLabel,
      isChecked,
      isPartialChecked,
      idsToSelect: objects.map(d => d[this.idKey]),
    }

    return {
      category,
      data: categoryObject,
      height: CATEGORY_ROW_HEIGHT,
      level,
    }
  }

  @action.bound
  public setSearch(search: string) {
    this.filter.searchKey = search
  }

  public get searchKey(): string {
    return this.filter.searchKey
  }

  public set searchKey(search: string) {
    this.filter.searchKey = search
  }

  public get columns(): ILWFCColumn[] {
    return [
      {
        dataKey: SubscribeUsersSelectorDataKeys.CHECKBOX,
        width: 40,
      },
      {
        dataKey: SubscribeUsersSelectorDataKeys.USER,
        label: Localization.translator.user,

        width: 360,
      },
    ]
  }
  public get rows(): ILWFCRow[] {
    return this.toBandTreeNodeRows(
      [this.groupingKey],
      null,
      '',
      0,
      false,
      this.sortTreeObjs,
    )
  }

  @action.bound
  public setGroupingKey(option: SubscribeUsersSelectorGroupingOption) {
    this.filter.groupingKey = option as unknown as string
    this.resetCollapsing()
  }

  @computed
  public get categoryToInstancesMap(): { [key: string]: User[] } {
    const map = {}

    this.filteredCollection.forEach(user => {
      const categoryIds = this.getCategoryIds(user, this.groupingKey)
      categoryIds.forEach(formattedCategoryId => {
        if (map[formattedCategoryId]) {
          map[formattedCategoryId].push(user)
        } else {
          map[formattedCategoryId] = [user]
        }
      })
    })

    return map
  }

  private getCategoryIds(user: User, band: string) {
    switch (band) {
      case SubscribeUsersSelectorGroupingOption.COMPANY:
        return [
          UserProject.getCompanyName(
            user,
            this.userProjectsStore,
            this.companiesStore,
          ),
        ]
      case SubscribeUsersSelectorGroupingOption.ROLE: {
        const roleIds = UserProject.getRolesIds(
          user,
          this.userProjectsStore,
          true,
        )
        const roleNames =
          roleIds
            ?.map(id => this.projectRolesStore?.getInstanceById(id)?.name || id)
            ?.filter(Boolean) || []
        return roleNames.length ? roleNames : [UNASSIGNED]
      }
      case SubscribeUsersSelectorGroupingOption.TEAM: {
        const teamIds = UserProject.getTeamsIds(user, this.userProjectsStore)
        const teamNames =
          teamIds
            ?.map(id => this.projectTeamsStore?.getInstanceById(id)?.name)
            ?.filter(Boolean) || []
        return teamNames.length ? teamNames : [UNASSIGNED]
      }
      case SubscribeUsersSelectorGroupingOption.TRADE: {
        const tradeIds = UserProject.getTradesIds(user, this.userProjectsStore)
        const tradeNames =
          tradeIds
            ?.map(id => this.projectTradesStore?.getInstanceById(id)?.name)
            ?.filter(Boolean) || []
        return tradeNames.length ? tradeNames : [UNASSIGNED]
      }
      case SubscribeUsersSelectorGroupingOption.ACCOUNT_POSITION: {
        const pos = user.accountPosition
        return pos ? [pos] : [UNASSIGNED]
      }
      case SubscribeUsersSelectorGroupingOption.NONE:
        return [SubscribeUsersSelectorGroupingOption.NONE]
      default:
        return [UNASSIGNED]
    }
  }

  protected toRows(instances: User[], _category?: string, level?: number) {
    return instances.map(user => {
      const isAssignedToAll = this.isUserAssignedToAllSelectedWorkflows(user.id)

      const isAssignedToAny = this.isUserAssignedToAnySelectedWorkflow(user.id)

      return {
        data: {
          id: user.id,
          [SubscribeUsersSelectorDataKeys.USER]: user,
          [SubscribeUsersSelectorDataKeys.CHECKBOX]: {
            value: isAssignedToAll || isAssignedToAny,
            isAssignedToAll,
            isAssignedToAny,
          },
        },
        level,
      }
    })
  }

  protected compareRows = (row1, row2): number => {
    const { columnKey, order } = this.sortState
    if (!columnKey) return 0

    let aVal = ''
    let bVal = ''

    if (columnKey === SubscribeUsersSelectorDataKeys.USER) {
      aVal =
        row1.data?.[
          SubscribeUsersSelectorDataKeys.USER
        ]?.fullName?.toString() || ''
      bVal =
        row2.data?.[
          SubscribeUsersSelectorDataKeys.USER
        ]?.fullName?.toString() || ''
    } else {
      aVal = (row1.data?.[columnKey] || '').toString()
      bVal = (row2.data?.[columnKey] || '').toString()
    }

    return aVal.localeCompare(bVal) * order
  }

  private sortTreeObjs = (treeNodeObjs: ITreeNodeObj[]) => {
    const map = new Map<string, ITreeNodeObj>()
    treeNodeObjs.forEach(obj => map.set(obj.id, obj))
    return Array.from(map.values()).sort((a, b) => {
      if (a.id === UNASSIGNED) return 1
      if (b.id === UNASSIGNED) return -1
      return (a.name || '').localeCompare(b.name || '')
    })
  }

  protected getTreeNodeObjsByBand(currentBand: string): ITreeNodeObj[] {
    switch (currentBand) {
      case SubscribeUsersSelectorGroupingOption.NONE:
        return [
          {
            id: SubscribeUsersSelectorGroupingOption.NONE,
            name: SubscribeUsersSelectorGroupingOption.NONE,
          },
        ]

      case SubscribeUsersSelectorGroupingOption.COMPANY:
        return [
          ...this.filteredCollection.map(user => {
            const companyId = UserProject.getCompanyId(
              user,
              this.userProjectsStore,
            )
            const companyName = UserProject.getCompanyName(
              user,
              this.userProjectsStore,
              this.companiesStore,
            )
            const company = this.companiesStore.getCompanyById(companyId)
            return {
              name: companyName,
              id: companyName,
              object: {
                icon: Avatar,
                iconProps: {
                  src: company?.avatarUrl,
                  size: AvatarSize.XMinuscule,
                },
              },
            }
          }),
          groupingCommonMapper(UNASSIGNED),
        ]
      case SubscribeUsersSelectorGroupingOption.ROLE: {
        const names = new Set<string>()
        this.filteredCollection.forEach(user => {
          this.getCategoryIds(user, currentBand).forEach(n => names.add(n))
        })
        return [
          ...Array.from(names).map(name => ({ id: name, name })),
          groupingCommonMapper(UNASSIGNED),
        ]
      }
      case SubscribeUsersSelectorGroupingOption.TEAM: {
        const names = new Set<string>()
        this.filteredCollection.forEach(user => {
          this.getCategoryIds(user, currentBand).forEach(n => names.add(n))
        })
        return [
          ...Array.from(names).map(name => ({ id: name, name })),
          groupingCommonMapper(UNASSIGNED),
        ]
      }
      case SubscribeUsersSelectorGroupingOption.TRADE: {
        const names = new Set<string>()
        this.filteredCollection.forEach(user => {
          this.getCategoryIds(user, currentBand).forEach(n => names.add(n))
        })
        return [
          ...Array.from(names).map(name => ({ id: name, name })),
          groupingCommonMapper(UNASSIGNED),
        ]
      }
      case SubscribeUsersSelectorGroupingOption.ACCOUNT_POSITION: {
        const names = new Set<string>()
        this.filteredCollection.forEach(user => {
          this.getCategoryIds(user, currentBand).forEach(n => names.add(n))
        })
        return [
          ...Array.from(names).map(name => ({ id: name, name })),
          groupingCommonMapper(UNASSIGNED),
        ]
      }
    }
  }

  @computed
  protected get bandObjectMap(): {
    [bandType: string]: { [groupName: string]: User[] }
  } {
    const map = {}

    const groupingOptions = Object.values(SubscribeUsersSelectorGroupingOption)

    groupingOptions.forEach(band => {
      const bandMap = (map[band] = {})

      this.filteredCollection.forEach(logistic => {
        const groupNames: string[] = this.getCategoryIds(logistic, band)
        if (!groupNames.length) {
          groupNames.push(UNASSIGNED)
        }

        groupNames.forEach(groupName => {
          if (!bandMap[groupName]) {
            bandMap[groupName] = []
          }

          bandMap[groupName].push(logistic)
        })
      })

      for (const name in bandMap) {
        bandMap[name].sort((a, b) => {
          const aName = (a.lastName || a.fullName || '').toString()
          const bName = (b.lastName || b.fullName || '').toString()
          return aName.localeCompare(bName)
        })
      }
    })

    return map
  }

  @computed
  public get hasPendingChanges(): boolean {
    if (!this.selectedWorkflows?.length) return false
    return this.selectedWorkflows.some(workflow => {
      const workflowId = workflow.id
      const draftSet = this.draftUserIdsByWorkflow.get(workflowId) || new Set()
      const currentSet = this.getCurrentUserIdsSet(workflowId)
      if (draftSet.size !== currentSet.size) return true
      for (const id of draftSet) {
        if (!currentSet.has(id)) return true
      }
      return false
    })
  }

  @action.bound
  public applyChanges() {
    if (!this.selectedWorkflows?.length) return

    const dtosToUpdate = []
    const idsToDelete = []

    this.selectedWorkflows.forEach(workflow => {
      const workflowId = workflow.id
      const draftSet = new Set(
        this.draftUserIdsByWorkflow.get(workflowId) || [],
      )
      const existing =
        this.workflowAssignmentsStore.getAssignmentByEntityId(workflowId)

      if (existing) {
        let recipientsByType = existing.recipients.find(
          r => r.type === RecipientType.User,
        )

        if (draftSet.size === 0) {
          // remove user recipients
          if (recipientsByType) {
            existing.recipients = existing.recipients.filter(
              r => r.type !== RecipientType.User,
            )
          }

          // delete entire assignment if empty and not a draft
          if (!existing.recipients.length && !existing.isDraft) {
            idsToDelete.push(existing.id)
          } else {
            dtosToUpdate.push(existing.getDto())
          }
          return
        }

        if (!recipientsByType) {
          recipientsByType = { type: RecipientType.User, ids: [] }
          existing.recipients.push(recipientsByType)
        }

        recipientsByType.ids = Array.from(draftSet)
        dtosToUpdate.push(existing.getDto())
        return
      }

      // No existing, but draft has users -> create new
      if (draftSet.size > 0) {
        const draft = Assignment.getDraft()
        draft.projectId = this.state.activeProject.id
        draft.recipients = [
          { type: RecipientType.User, ids: Array.from(draftSet) },
        ]
        dtosToUpdate.push(draft.getDto(workflowId))
      }
    })

    if (idsToDelete.length) {
      this.workflowAssignmentsStore.delete(idsToDelete)
    }
    if (dtosToUpdate.length) {
      this.workflowAssignmentsStore.save(dtosToUpdate)
    }

    this.resetDraftFromStore()
  }

  @action.bound
  public discardChanges() {
    this.resetDraftFromStore()
  }

  private getCurrentUserIdsSet(entityId: string): Set<string> {
    const assignment =
      this.workflowAssignmentsStore.getAssignmentByEntityId(entityId)
    if (!assignment) return new Set()
    const recipientsByType = assignment.recipients.find(
      r => r.type === RecipientType.User,
    )
    return new Set(recipientsByType?.ids || [])
  }

  @action.bound
  private resetDraftFromStore() {
    this.draftUserIdsByWorkflow = new Map()
    if (!this.selectedWorkflows?.length) return
    this.selectedWorkflows.forEach(workflow => {
      const workflowId = workflow.id
      const set = this.getCurrentUserIdsSet(workflowId)
      this.draftUserIdsByWorkflow.set(workflowId, set)
    })
  }
}

export default SubscribeUsersSelectorStore
